import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Users, TrendingUp, Clock, CheckCircle2, AlertTriangle, Calendar, RefreshCw, Download, Factory, Gauge, Wrench, Target, Timer, Package, AlertCircle } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

const getEfficiencyColor = (value) => {
  if (value >= 10) return 'text-green-600';
  if (value >= 5) return 'text-yellow-600';
  if (value >= 2) return 'text-orange-600';
  return 'text-red-600';
};

const getEfficiencyBadge = (value) => {
  if (value >= 10) return 'bg-green-100 text-green-800';
  if (value >= 5) return 'bg-yellow-100 text-yellow-800';
  if (value >= 2) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

const getDefectBadge = (rate) => {
  if (rate <= 1) return 'bg-green-100 text-green-800';
  if (rate <= 5) return 'bg-yellow-100 text-yellow-800';
  if (rate <= 10) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

const EfficiencyBar = ({ value, maxValue = 20 }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  const barColor = value >= 10 ? 'bg-green-600' : 
                   value >= 5 ? 'bg-yellow-600' : 
                   value >= 2 ? 'bg-orange-600' : 'bg-red-600';
  
  return (
    <div className="w-24">
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${barColor} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default function WorkerProductivityReportPage() {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('workers');
  
  // Filter states
  const [periodType, setPeriodType] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [sortBy, setSortBy] = useState('units_per_hour');
  const [sortOrder, setSortOrder] = useState('desc');

  const fetchProductivityData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      
      if (periodType === 'month') {
        params.append('month', selectedMonth);
        params.append('year', selectedYear);
      } else if (periodType === 'custom' && customStartDate && customEndDate) {
        params.append('start_date', new Date(customStartDate).toISOString());
        params.append('end_date', new Date(customEndDate).toISOString());
      }
      
      const response = await axios.get(`${API_URL}/mis/shopfloor-productivity?${params}`, { headers: getAuthHeader() });
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch productivity data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [periodType, selectedMonth, selectedYear, customStartDate, customEndDate, getAuthHeader]);

  useEffect(() => {
    fetchProductivityData();
  }, [fetchProductivityData]);

  // Sort workers by selected criteria
  const sortedWorkers = data?.workers ? [...data.workers].sort((a, b) => {
    let valA, valB;
    switch (sortBy) {
      case 'units_per_hour':
        valA = a.units_per_hour || 0;
        valB = b.units_per_hour || 0;
        break;
      case 'name':
        valA = a.worker_name || '';
        valB = b.worker_name || '';
        return sortOrder === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'units':
        valA = a.total_units_produced || 0;
        valB = b.total_units_produced || 0;
        break;
      case 'time':
        valA = a.total_time_hours || 0;
        valB = b.total_time_hours || 0;
        break;
      case 'jobs':
        valA = a.total_jobs || 0;
        valB = b.total_jobs || 0;
        break;
      case 'defects':
        valA = a.defect_rate || 0;
        valB = b.defect_rate || 0;
        break;
      default:
        valA = a.units_per_hour || 0;
        valB = b.units_per_hour || 0;
    }
    return sortOrder === 'asc' ? valA - valB : valB - valA;
  }) : [];

  const summary = data?.summary || {
    total_workers: 0,
    total_jobs: 0,
    total_units_produced: 0,
    total_time_hours: 0,
    overall_units_per_hour: 0,
    total_defects: 0,
    overall_defect_rate: 0,
    unique_processes: 0
  };

  const exportToCSV = () => {
    if (!sortedWorkers.length) return;
    
    const headers = ['Worker Name', 'Role', 'Total Jobs', 'Jobs Completed', 'Units Produced', 'Time (Hours)', 'Units/Hour', 'Defect Rate (%)', 'Processes'];
    const rows = sortedWorkers.map(w => [
      w.worker_name,
      w.role,
      w.total_jobs,
      w.jobs_completed,
      w.total_units_produced,
      w.total_time_hours,
      w.units_per_hour,
      w.defect_rate,
      w.processes_worked?.join('; ')
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shopfloor_productivity_${selectedMonth}_${selectedYear}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="shopfloor-productivity-report">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Factory className="w-7 h-7 text-indigo-600" />
              Shopfloor Worker Productivity
            </h1>
            <p className="text-muted-foreground">Track factory worker performance based on Job Card data</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={exportToCSV} variant="outline" disabled={!sortedWorkers.length}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={fetchProductivityData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Report Period
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="space-y-2">
                <Label>Period Type</Label>
                <Select value={periodType} onValueChange={setPeriodType}>
                  <SelectTrigger className="w-32" data-testid="period-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">Monthly</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {periodType === 'month' && (
                <>
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map(m => (
                          <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map(y => (
                          <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {periodType === 'custom' && (
                <>
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Sort By</Label>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="units_per_hour">Efficiency</SelectItem>
                    <SelectItem value="name">Name</SelectItem>
                    <SelectItem value="units">Units Produced</SelectItem>
                    <SelectItem value="time">Time Spent</SelectItem>
                    <SelectItem value="jobs">Total Jobs</SelectItem>
                    <SelectItem value="defects">Defect Rate</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button variant="outline" size="icon" onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}>
                {sortOrder === 'asc' ? '↑' : '↓'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Workers</p>
                  <p className="text-xl font-bold">{summary.total_workers}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Wrench className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Jobs</p>
                  <p className="text-xl font-bold">{summary.total_jobs}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Package className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Units Produced</p>
                  <p className="text-xl font-bold">{summary.total_units_produced.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Timer className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Hours</p>
                  <p className="text-xl font-bold">{summary.total_time_hours.toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Gauge className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Avg Units/Hr</p>
                  <p className={`text-xl font-bold ${getEfficiencyColor(summary.overall_units_per_hour)}`}>
                    {summary.overall_units_per_hour}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Defects</p>
                  <p className="text-xl font-bold text-red-600">{summary.total_defects}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Defect Rate</p>
                  <p className="text-xl font-bold">{summary.overall_defect_rate}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Target className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Processes</p>
                  <p className="text-xl font-bold">{summary.unique_processes}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Workers and Process View */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="workers" data-testid="tab-workers">
              <Users className="w-4 h-4 mr-1" />
              By Worker ({sortedWorkers.length})
            </TabsTrigger>
            <TabsTrigger value="process" data-testid="tab-process">
              <Wrench className="w-4 h-4 mr-1" />
              By Process ({data?.by_process?.length || 0})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="workers" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Worker Performance Details</CardTitle>
                <CardDescription>
                  {periodType === 'month' && `${MONTHS.find(m => m.value === selectedMonth)?.label} ${selectedYear}`}
                  {periodType === 'custom' && customStartDate && customEndDate && `${customStartDate} to ${customEndDate}`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12 text-muted-foreground">Loading productivity data...</div>
                ) : sortedWorkers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Factory className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No job card data for this period</p>
                    <p className="text-sm">Workers appear here when assigned to Job Cards</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rank</TableHead>
                        <TableHead>Worker</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="text-center">Jobs</TableHead>
                        <TableHead className="text-center">Completed</TableHead>
                        <TableHead className="text-right">Units Produced</TableHead>
                        <TableHead className="text-right">Time (Hrs)</TableHead>
                        <TableHead className="text-center">Units/Hour</TableHead>
                        <TableHead className="text-center">Defect Rate</TableHead>
                        <TableHead className="text-center">Processes</TableHead>
                        <TableHead className="w-28">Efficiency</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedWorkers.map((worker, idx) => (
                        <TableRow key={worker.worker_id || idx} data-testid={`worker-row-${worker.worker_id}`}>
                          <TableCell>
                            {idx < 3 && worker.units_per_hour > 0 ? (
                              <Badge className={idx === 0 ? 'bg-yellow-400 text-yellow-900' : idx === 1 ? 'bg-gray-300 text-gray-800' : 'bg-orange-300 text-orange-900'}>
                                #{idx + 1}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">{idx + 1}</span>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{worker.worker_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{worker.role}</Badge>
                          </TableCell>
                          <TableCell className="text-center">{worker.total_jobs}</TableCell>
                          <TableCell className="text-center">
                            <span className={worker.jobs_completed === worker.total_jobs ? 'text-green-600' : ''}>
                              {worker.jobs_completed}/{worker.total_jobs}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-medium">{worker.total_units_produced.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{worker.total_time_hours}</TableCell>
                          <TableCell className="text-center">
                            <Badge className={getEfficiencyBadge(worker.units_per_hour)}>
                              {worker.units_per_hour}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={getDefectBadge(worker.defect_rate)}>
                              {worker.defect_rate}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{worker.processes_count}</TableCell>
                          <TableCell>
                            <EfficiencyBar value={worker.units_per_hour} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="process" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle>Process Performance Summary</CardTitle>
                <CardDescription>Productivity metrics aggregated by process</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-12 text-muted-foreground">Loading...</div>
                ) : !data?.by_process?.length ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Wrench className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No process data for this period</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Process</TableHead>
                        <TableHead className="text-center">Total Jobs</TableHead>
                        <TableHead className="text-right">Total Units</TableHead>
                        <TableHead className="text-right">Total Hours</TableHead>
                        <TableHead className="text-center">Units/Hour</TableHead>
                        <TableHead className="text-center">Defect Rate</TableHead>
                        <TableHead className="text-center">Workers</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.by_process.map((proc, idx) => (
                        <TableRow key={proc.process_name || idx}>
                          <TableCell className="font-medium">{proc.process_name}</TableCell>
                          <TableCell className="text-center">{proc.total_jobs}</TableCell>
                          <TableCell className="text-right">{proc.total_units.toLocaleString()}</TableCell>
                          <TableCell className="text-right">{proc.total_time_hours}</TableCell>
                          <TableCell className="text-center">
                            <Badge className={getEfficiencyBadge(proc.units_per_hour)}>
                              {proc.units_per_hour}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge className={getDefectBadge(proc.defect_rate)}>
                              {proc.defect_rate}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">{proc.worker_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
